/**
 * Admin CSV Lead Import (P3).
 * Supporta CSV/TSV con colonne compatibili con Google Sheets export.
 */
import React, { useState } from 'react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Play } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminLeadsImport = () => {
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [source, setSource] = useState('csv_import');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const buildFormData = () => {
    const fd = new FormData();
    fd.append('file', file);
    return fd;
  };

  const doPreview = async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/admin/leads-import/preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken}` },
        body: buildFormData(),
      });
      const j = await res.json();
      if (res.ok) setPreview(j);
      else toast({ title: 'Errore anteprima', description: j.detail || 'Errore', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const doImport = async () => {
    if (!file) return;
    if (!window.confirm('Confermi l\'import? Verranno creati/aggiornati i lead nel CRM.')) return;
    setLoading(true);
    try {
      const fd = buildFormData();
      fd.append('default_source', source || 'csv_import');
      const res = await fetch(`${API}/api/admin/leads-import/execute`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken}` },
        body: fd,
      });
      const j = await res.json();
      if (res.ok) {
        setResult(j);
        toast({ title: 'Import completato', description: `Creati ${j.created}, aggiornati ${j.updated}, saltati ${j.skipped}` });
      } else {
        toast({ title: 'Errore', description: j.detail || 'Errore', variant: 'destructive' });
      }
    } finally { setLoading(false); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Import Lead da CSV</h1>
          <p className="mt-1 text-gray-600">Carica un file CSV/TSV (es. export Google Sheets) per importare lead nel CRM.</p>
        </div>

        <div className="bg-white rounded-2xl border border-purple-100 p-6">
          <label className="block">
            <span className="text-xs font-semibold text-gray-700 uppercase">File CSV/TSV</span>
            <div className="mt-2 flex items-center gap-3">
              <input type="file" accept=".csv,.tsv,.txt"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setResult(null); }}
                data-testid="import-file-input"
                className="text-sm" />
              {file && <span className="text-xs text-gray-500 inline-flex items-center gap-1"><FileSpreadsheet className="w-4 h-4" /> {file.name}</span>}
            </div>
          </label>

          <label className="block mt-4">
            <span className="text-xs font-semibold text-gray-700 uppercase">Sorgente lead (leadSource)</span>
            <input type="text" value={source} onChange={(e) => setSource(e.target.value)}
              placeholder="csv_import" data-testid="import-source-input"
              className="mt-1 block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md text-sm" />
          </label>

          <div className="mt-6 flex gap-2">
            <button onClick={doPreview} disabled={!file || loading} data-testid="btn-preview"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-md hover:bg-purple-50 disabled:opacity-50">
              <Upload className="w-4 h-4" /> Anteprima
            </button>
            <button onClick={doImport} disabled={!file || loading} data-testid="btn-import"
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50">
              <Play className="w-4 h-4" /> Importa
            </button>
          </div>
        </div>

        {preview && (
          <div className="bg-white rounded-2xl border p-6" data-testid="preview-panel">
            <h3 className="font-semibold mb-3 text-gray-900">Anteprima file</h3>
            <div className="text-xs space-y-2">
              <div><b>Colonne rilevate:</b> {(preview.headers || []).join(', ') || '—'}</div>
              {preview.unknown_headers?.length > 0 && (
                <div className="text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                  Colonne non riconosciute (verranno ignorate): {preview.unknown_headers.join(', ')}
                </div>
              )}
              <details className="text-xs mt-2">
                <summary className="cursor-pointer text-purple-600">Campi supportati (mappa colonne → campo canonico)</summary>
                <div className="mt-2 grid grid-cols-2 gap-1 text-gray-600">
                  {(preview.supported_fields || []).map((f) => <div key={f} className="font-mono">{f}</div>)}
                </div>
              </details>
              <div className="mt-3">
                <b>Prime righe (mappate):</b>
                <pre className="mt-1 p-3 bg-gray-50 rounded text-[10px] overflow-x-auto">{JSON.stringify(preview.sample_rows, null, 2)}</pre>
              </div>
            </div>
          </div>
        )}

        {result && (
          <div className="bg-white rounded-2xl border p-6" data-testid="result-panel">
            <h3 className="font-semibold mb-3 text-gray-900 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" /> Import completato
            </h3>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <div className="text-xs text-green-700 font-semibold">Creati</div>
                <div className="text-2xl font-bold text-green-800 tabular-nums">{result.created}</div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <div className="text-xs text-blue-700 font-semibold">Aggiornati</div>
                <div className="text-2xl font-bold text-blue-800 tabular-nums">{result.updated}</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded p-3">
                <div className="text-xs text-amber-700 font-semibold">Saltati</div>
                <div className="text-2xl font-bold text-amber-800 tabular-nums">{result.skipped}</div>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="mt-4">
                <b className="text-xs">Errori (prime {result.errors.length}):</b>
                <ul className="mt-1 text-xs text-red-700 list-disc ml-5">
                  {result.errors.map((e, i) => (
                    <li key={i}>Riga {e.row}: {e.reason}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 text-xs text-blue-800">
          <b>Come esportare da Google Sheets:</b> File → Scarica → Valori separati da virgole (CSV). Le colonne supportate accettano nomi in italiano ({" "}
          <code className="font-mono">nome, cognome, email, telefono, azienda, sorgente, indirizzo, note, tag</code>) o inglese (
          <code className="font-mono">firstName, lastName, email, phone, companyName, leadSource…</code>). L'import è <b>idempotente</b>: se un lead con la stessa email/telefono esiste già, verrà aggiornato invece di duplicato.
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminLeadsImport;
